import { m as motion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';
import { useMediaQueryDeviceStateValue } from '@/shared/atoms/media-query-device';
import Button from '@/shared/components/shared/Button';
import Link from '@/shared/components/shared/Link';
import Lottie from '@/shared/components/shared/Lottie';
import { useTypograf } from '@/shared/hooks/use-typograf';
import { VideoShape } from '@/shared/lib/types';
import { DEFAULT_ECOSYSTEM_ITEMS } from '../EcosystemSection/ecosystem-data';
import LiveVideoClipSection from '../LiveVideoClipSection';
import VideoClipSection from '../VideoClipSection';

const LIVE_LOTTIE = DEFAULT_ECOSYSTEM_ITEMS[2].lottie;

export type LiveGradientSectionProps = {
    slogan: string;
    location: string;
    link: {
        name: string;
        href: string;
    };
    shortVideo: VideoShape;
    longVideo: VideoShape;
};

type Props = React.HTMLAttributes<HTMLElement> & {
    liveGradientSectionProps: LiveGradientSectionProps;
};

const PHASE_1_END = 0.22;
const PHASE_2_END = 0.38;
const PHASE_3_END = 0.78;

type HeroContentProps = {
    tpSlogan: string;
    tpLocation: string;
    tpLinkName: string;
    linkHref: string;
};

const HeroContent = ({ tpSlogan, tpLocation, tpLinkName, linkHref }: HeroContentProps) => (
    <>
        <h1 className="live-gradient-section-titles h1-small">
            <div className="live-gradient-section-item">
                <div className="live-gradient-section-title">TNF</div>
            </div>
            <div className="live-gradient-section-item live-gradient-section-item--right">
                <div className="live-gradient-section-title">LIVE</div>
            </div>
        </h1>
        <div className="live-gradient-section-scrollable">
            <div className="live-gradient-section-center">
                <div className="ecosystem-section__pictogram">
                    <Lottie src={LIVE_LOTTIE.src} className="ecosystem-section__pictogram-img img-fluid" />
                </div>
            </div>
            <div className="live-gradient-section-info">
                {tpSlogan && (
                    <div
                        className="live-gradient-section-slogan text-l wysiwyg"
                        dangerouslySetInnerHTML={{ __html: tpSlogan }}
                    />
                )}
                {tpLocation && (
                    <div
                        className="live-gradient-section-date text-m wysiwyg"
                        dangerouslySetInnerHTML={{ __html: tpLocation }}
                    />
                )}
                {tpLinkName && linkHref && (
                    <Button tag={Link} href={linkHref} className="live-gradient-section-button" variant="outline-light">
                        {tpLinkName}
                    </Button>
                )}
            </div>
        </div>
    </>
);

const LiveGradientSection = ({ liveGradientSectionProps }: Props) => {
    const device = useMediaQueryDeviceStateValue();
    const isDesktop = device === 'desktop';

    const wrapperRef = useRef<HTMLDivElement>(null);
    const { scrollYProgress } = useScroll({ target: wrapperRef });

    const scrollableTranslate = useTransform(scrollYProgress, [0, PHASE_1_END], [0, -1600]);
    const titleSpreadAmount = 600;
    const titleSpreadLeft = useTransform(scrollYProgress, [PHASE_2_END, 1], [0, -titleSpreadAmount]);
    const titleSpreadRight = useTransform(scrollYProgress, [PHASE_2_END, 1], [0, titleSpreadAmount]);
    const titleOpacity = useTransform(scrollYProgress, [PHASE_3_END, 1], [1, 0]);

    const tpSlogan = useTypograf(liveGradientSectionProps.slogan);
    const tpLocation = useTypograf(liveGradientSectionProps.location);
    const tpLinkName = useTypograf(liveGradientSectionProps.link.name);

    const heroProps = {
        tpSlogan,
        tpLocation,
        tpLinkName,
        linkHref: liveGradientSectionProps.link.href,
    };

    if (!isDesktop) {
        return (
            <section
                className="live-gradient-section-wrapper js-header-theme-trigger"
                data-theme="light"
                ref={wrapperRef}
            >
                {liveGradientSectionProps.slogan && (
                    <div className="live-gradient-section-bottom">
                        <HeroContent {...heroProps} />
                    </div>
                )}
                {liveGradientSectionProps.shortVideo[0]?.src && (
                    <VideoClipSection
                        scrollYProgress={scrollYProgress}
                        shortVideo={liveGradientSectionProps.shortVideo[0].src}
                        longVideo={liveGradientSectionProps.longVideo[0]?.src}
                    />
                )}
            </section>
        );
    }

    return (
        <section className="live-gradient-section-wrapper js-header-theme-trigger" data-theme="light" ref={wrapperRef}>
            <div className="live-gradient-section-sticky">
                <h1 className="live-gradient-section-titles h1-small">
                    <motion.div
                        className="live-gradient-section-item"
                        style={{ x: titleSpreadLeft, opacity: titleOpacity }}
                    >
                        <div className="live-gradient-section-title">TNF</div>
                    </motion.div>
                    <motion.div
                        className="live-gradient-section-item live-gradient-section-item--right"
                        style={{ x: titleSpreadRight, opacity: titleOpacity }}
                    >
                        <div className="live-gradient-section-title">LIVE</div>
                    </motion.div>
                </h1>
                <motion.div className="live-gradient-section-scrollable" style={{ y: scrollableTranslate }}>
                    <div className="live-gradient-section-center">
                        <div className="ecosystem-section__pictogram">
                            <Lottie src={LIVE_LOTTIE.src} className="ecosystem-section__pictogram-img img-fluid" />
                        </div>
                    </div>
                    <div className="live-gradient-section-info">
                        {tpSlogan && (
                            <div
                                className="live-gradient-section-slogan text-l wysiwyg"
                                dangerouslySetInnerHTML={{ __html: tpSlogan }}
                            />
                        )}
                        {tpLocation && (
                            <div
                                className="live-gradient-section-date text-m wysiwyg"
                                dangerouslySetInnerHTML={{ __html: tpLocation }}
                            />
                        )}
                        {tpLinkName && liveGradientSectionProps.link.href && (
                            <Button
                                tag={Link}
                                href={liveGradientSectionProps.link.href}
                                className="live-gradient-section-button"
                                variant="outline-light"
                            >
                                {tpLinkName}
                            </Button>
                        )}
                    </div>
                </motion.div>
                {liveGradientSectionProps.shortVideo[0]?.src && (
                    <LiveVideoClipSection
                        scrollYProgress={scrollYProgress}
                        phase1End={PHASE_1_END}
                        phase2End={PHASE_2_END}
                        phase3End={PHASE_3_END}
                        shortVideo={liveGradientSectionProps.shortVideo[0].src}
                        longVideo={liveGradientSectionProps.longVideo[0]?.src}
                    />
                )}
            </div>
        </section>
    );
};

export default LiveGradientSection;
